/**
 * Array normalization utilities for Player API (BEWOTEC SOAP)
 *
 * SOAP APIs often return single items as objects instead of arrays.
 * These utilities normalize responses to always use consistent array types.
 *
 * @example
 * // SOAP may return single item as object:
 * { Product: { Id: 1, Name: "Hotel" } }
 * // Or multiple items as array:
 * { Product: [{ Id: 1 }, { Id: 2 }] }
 *
 * // normalizeToArray handles both cases:
 * const products = normalizeToArray(result.Products?.Product); // always T[]
 */

import type {
  GetProductListResult,
  GetProductOffersResult,
  GetAvailableValuesResult,
} from '@/types/schemas';

/**
 * Ensures value is always an array
 *
 * Handles SOAP API responses that return single items as objects.
 *
 * @example
 * normalizeToArray(undefined) // []
 * normalizeToArray(null) // []
 * normalizeToArray({ id: 1 }) // [{ id: 1 }]
 * normalizeToArray([{ id: 1 }, { id: 2 }]) // [{ id: 1 }, { id: 2 }]
 */
export function normalizeToArray<T>(value: T | T[] | undefined | null): T[] {
  if (value === undefined || value === null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

// Bewotec returns "Accomodation" (one m) for AccommodationTargetgroups in both
// the static AccommodationProperties list and the dynamic Filters element.
const BEWOTEC_KEY_TYPO_PREFIX = 'AccomodationTargetgroups.';
const BEWOTEC_KEY_FIXED_PREFIX = 'AccommodationTargetgroups.';

export function fixBewotecAttributeKey(key: string): string {
  return key.startsWith(BEWOTEC_KEY_TYPO_PREFIX)
    ? BEWOTEC_KEY_FIXED_PREFIX + key.slice(BEWOTEC_KEY_TYPO_PREFIX.length)
    : key;
}

/**
 * Normalizes GetProductListResult arrays
 *
 * Ensures all nested arrays are consistently typed as T[] instead of T | T[].
 * Also normalizes deeply nested arrays in Services and Products.
 *
 * @example
 * const result = await getProductList(params);
 * const normalized = normalizeProductListResult(result);
 * // normalized.Products.Product is always Product[]
 * // normalized.Services.Service is always Service[]
 */
export function normalizeProductListResult(result: GetProductListResult): GetProductListResult {
  const normalized = {
    ...result,
    Products: result.Products
      ? {
          Product: normalizeToArray(result.Products.Product).map((product) => ({
            ...product,
            ServiceReferences: product.ServiceReferences
              ? {
                  ServiceReference: normalizeToArray(
                    product.ServiceReferences.ServiceReference
                  ).map((ref) => ({
                    ...ref,
                    PassengerIds: ref.PassengerIds
                      ? {
                          PassengerId: normalizeToArray(ref.PassengerIds.PassengerId),
                        }
                      : ref.PassengerIds,
                  })),
                }
              : product.ServiceReferences,
            PriceInformation: product.PriceInformation
              ? {
                  ...product.PriceInformation,
                  Passengers: product.PriceInformation.Passengers
                    ? {
                        PriceInformationPassenger: normalizeToArray(
                          product.PriceInformation.Passengers.PriceInformationPassenger
                        ),
                      }
                    : product.PriceInformation.Passengers,
                  PriceTypes: product.PriceInformation.PriceTypes
                    ? {
                        PriceType: normalizeToArray(product.PriceInformation.PriceTypes.PriceType),
                      }
                    : product.PriceInformation.PriceTypes,
                  PriceDetails: product.PriceInformation.PriceDetails
                    ? {
                        PriceDetail: normalizeToArray(
                          product.PriceInformation.PriceDetails.PriceDetail
                        ),
                      }
                    : product.PriceInformation.PriceDetails,
                }
              : product.PriceInformation,
          })),
        }
      : result.Products,
    Filters: result.Filters
      ? {
          ...result.Filters,
          AttributesOverview: result.Filters.AttributesOverview
            ? {
                Attribute: normalizeToArray(result.Filters.AttributesOverview.Attribute).map(
                  (a) => ({
                    ...a,
                    Attribute: a.Attribute ? fixBewotecAttributeKey(a.Attribute) : a.Attribute,
                  })
                ),
              }
            : result.Filters.AttributesOverview,
        }
      : result.Filters,
    Services: result.Services
      ? {
          Service: normalizeToArray(result.Services.Service).map((service) => {
            const accommodationService = service.AccommodationService;

            return {
              ...service,
              AccommodationService: accommodationService
                ? {
                    ...accommodationService,
                    Identities: accommodationService.Identities
                      ? {
                          Identity: normalizeToArray(accommodationService.Identities.Identity),
                        }
                      : accommodationService.Identities,
                    AccommodationSpecifics: accommodationService.AccommodationSpecifics
                      ? {
                          AccommodationSpecific: normalizeToArray(
                            accommodationService.AccommodationSpecifics.AccommodationSpecific
                          ),
                        }
                      : accommodationService.AccommodationSpecifics,
                    AccommodationVariants: accommodationService.AccommodationVariants
                      ? {
                          AccommodationVariant: normalizeToArray(
                            accommodationService.AccommodationVariants.AccommodationVariant
                          ).map((variant) => ({
                            ...variant,
                            Rooms: variant.Rooms
                              ? {
                                  Room: normalizeToArray(variant.Rooms.Room),
                                }
                              : variant.Rooms,
                            Boards: variant.Boards
                              ? {
                                  Board: normalizeToArray(variant.Boards.Board),
                                }
                              : variant.Boards,
                          })),
                        }
                      : accommodationService.AccommodationVariants,
                  }
                : accommodationService,
            };
          }),
        }
      : result.Services,
  };

  return normalized;
}

/**
 * Normalizes GetProductOffersResult arrays
 *
 * Ensures all nested arrays are consistently typed as T[] instead of T | T[].
 *
 * @example
 * const result = await getProductOffers(params);
 * const normalized = normalizeProductOffersResult(result);
 * // normalized.Products.Product is always Product[]
 * // normalized.Services.Service is always Service[]
 */
export function normalizeProductOffersResult(
  result: GetProductOffersResult
): GetProductOffersResult {
  return {
    ...result,
    Products: result.Products
      ? {
          Product: normalizeToArray(result.Products.Product).map((product) => ({
            ...product,
            ServiceReferences: product.ServiceReferences
              ? {
                  ServiceReference: normalizeToArray(
                    product.ServiceReferences.ServiceReference
                  ).map((ref) => ({
                    ...ref,
                    PassengerIds: ref.PassengerIds
                      ? {
                          PassengerId: normalizeToArray(ref.PassengerIds.PassengerId),
                        }
                      : ref.PassengerIds,
                  })),
                }
              : product.ServiceReferences,
          })),
        }
      : result.Products,
    Services: result.Services
      ? {
          Service: normalizeToArray(result.Services.Service),
        }
      : result.Services,
  };
}

/**
 * Normalizes GetAvailableValuesResult arrays
 *
 * Ensures all nested arrays are consistently typed as T[] instead of T | T[].
 *
 * @example
 * const result = await getAvailableValues();
 * const normalized = normalizeAvailableValuesResult(result);
 * // normalized.Countries.Country is always Country[]
 * // normalized.Hotels.Hotel is always Hotel[]
 */
export function normalizeAvailableValuesResult(
  result: GetAvailableValuesResult
): GetAvailableValuesResult {
  return {
    ...result,
    Countries: result.Countries
      ? {
          Country: normalizeToArray(result.Countries.Country),
        }
      : result.Countries,
    Regions: result.Regions
      ? {
          Region: normalizeToArray(result.Regions.Region),
        }
      : result.Regions,
    Destinations: result.Destinations
      ? {
          Destination: normalizeToArray(result.Destinations.Destination),
        }
      : result.Destinations,
    Hotels: result.Hotels
      ? {
          Hotel: normalizeToArray(result.Hotels.Hotel),
        }
      : result.Hotels,
    BoardTypes: result.BoardTypes
      ? {
          BoardType: normalizeToArray(result.BoardTypes.BoardType),
        }
      : result.BoardTypes,
    RoomTypes: result.RoomTypes
      ? {
          RoomType: normalizeToArray(result.RoomTypes.RoomType),
        }
      : result.RoomTypes,
    AccommodationProperties: result.AccommodationProperties
      ? {
          AccommodationProperty: normalizeToArray(
            result.AccommodationProperties.AccommodationProperty
          ).map(fixBewotecAttributeKey),
        }
      : result.AccommodationProperties,
    ProductGroups: result.ProductGroups
      ? {
          ProductGroup: normalizeToArray(result.ProductGroups.ProductGroup),
        }
      : result.ProductGroups,
    ArrivalAirports: result.ArrivalAirports
      ? {
          Airport: normalizeToArray(result.ArrivalAirports.Airport),
        }
      : result.ArrivalAirports,
    HotelNames: result.HotelNames
      ? {
          HotelName: normalizeToArray(result.HotelNames.HotelName),
        }
      : result.HotelNames,
    Cities: result.Cities
      ? {
          City: normalizeToArray(result.Cities.City),
        }
      : result.Cities,
    TouroperatorCodes: result.TouroperatorCodes
      ? {
          TouroperatorCode: normalizeToArray(result.TouroperatorCodes.TouroperatorCode),
        }
      : result.TouroperatorCodes,
    PriceTypes: result.PriceTypes
      ? {
          PriceType: normalizeToArray(result.PriceTypes.PriceType),
        }
      : result.PriceTypes,
  };
}

/**
 * Checks if API result indicates success
 *
 * @example
 * if (isSuccessResult(result)) {
 *   // Process successful result
 * } else {
 *   // Handle error
 * }
 */
export function isSuccessResult(
  result:
    | GetProductListResult
    | GetProductOffersResult
    | GetAvailableValuesResult
    | null
    | undefined
): boolean {
  return result?.Success === true;
}

/**
 * Extracts error messages from ProcessMessages
 *
 * Filters out only Error and ConfigurationError messages.
 *
 * @example
 * const errors = extractErrorMessages(result);
 * if (errors.length > 0) {
 *   console.error('API errors:', errors.join(', '));
 * }
 */
export function extractErrorMessages(
  result:
    | GetProductListResult
    | GetProductOffersResult
    | GetAvailableValuesResult
    | null
    | undefined
): string[] {
  if (!result?.ProcessMessages?.ProcessMessage) {
    return [];
  }

  return result.ProcessMessages.ProcessMessage.filter(
    (msg) => msg.MessageType === 'Error' || msg.MessageType === 'ConfigurationError'
  )
    .map((msg) => msg.Message || 'Unknown error')
    .filter(Boolean);
}
